import { Menu, MenuProps, Select } from 'antd';
import Sider from 'antd/es/layout/Sider';
import React, { useEffect, useState } from 'react';
import {
  AimOutlined,
  AppstoreOutlined,
  AuditOutlined,
  BellOutlined,
  PartitionOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import './SiderMenu.scss';
import { alarmPath, devicesPath, locationInstallationPath, locationPath, tenantsPath, usersPath } from '../../routes';
import { useTranslation } from '../../i18n/i18n';
import ProfileDropdown from '../header/ProfileDropdown/ProfileDropdown';
import useTenantStore, { ALL_TENANTS_VALUE } from '../../stores/tenant.store';
import useAuthStore from '../../stores/auth.store';

type MenuItem = Required<MenuProps>['items'][number];

function getItem(label: React.ReactNode, key: React.Key, icon?: React.ReactNode, children?: MenuItem[]): MenuItem {
  return {
    key,
    icon,
    children,
    label,
  } as MenuItem;
}

interface SiderMenuProps {
  email?: string;
  userName: string;
}

const SiderMenu = ({ email, userName }: SiderMenuProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation('common', { keyPrefix: 'sidebar' });

  const isSuperAdminOrCompanyAdmin = useAuthStore((state) => state.isSuperAdminOrCompanyAdmin);
  const [collapsed, setCollapsed] = useState(false);
  let isDropdownClicked = false;

  const { tenants, selectedTenant, fetchTenants, setSelectedTenant } = useTenantStore();
  const isSuperAdmin = useAuthStore((state) => state.isSuperAdmin);

  useEffect(() => {
    fetchTenants();
  }, [fetchTenants]);

  const [selectedKey, setSelectedKey] = useState<string>(location.pathname.split('/').filter(Boolean).pop() || '');

  useEffect(() => {
    setSelectedKey(location.pathname.split('/').filter(Boolean).pop() || '');
  }, [location.pathname]);

  const handleMenu: MenuProps['onClick'] = (e) => {
    if (isDropdownClicked) return;
    setSelectedKey(e.key);
    navigate(`/dashboard/${e.key}`);
  };

  const menuItems = [
    getItem(t('instalationLocations'), locationInstallationPath, <PartitionOutlined />),
    getItem(t('devices'), devicesPath, <AppstoreOutlined />),
    getItem(t('location'), locationPath, <AimOutlined />),
    ...(isSuperAdminOrCompanyAdmin ? [getItem(t('users'), usersPath, <TeamOutlined />)] : []),
    getItem(t('alarms'), alarmPath, <BellOutlined />),
    ...(isSuperAdmin && selectedTenant === ALL_TENANTS_VALUE
      ? [getItem(t('tenants'), tenantsPath, <AuditOutlined />)]
      : []),
  ];

  const tenantOptions = isSuperAdmin ? [...tenants, { value: ALL_TENANTS_VALUE, label: t('all') }] : tenants;

  return (
    <Sider
      className={'SiderMenu'}
      theme="light"
      width={240}
      collapsible
      collapsed={collapsed}
      onCollapse={(value) => setCollapsed(value)}
    >
      {collapsed ? (
        <img className="Sider__logo__collapsed" src={'/assets/images/Kairos-k.png'} alt="logo" />
      ) : (
        <img className="Sider__logo" src={'/assets/images/KAIROS-logo.png'} alt="logo" />
      )}
      {isSuperAdmin && !collapsed && (
        <Select
          className={'SiderMenu__selector'}
          value={selectedTenant}
          options={tenantOptions}
          onChange={setSelectedTenant}
        />
      )}
      <Menu
        theme="light"
        mode="inline"
        items={menuItems}
        onClick={handleMenu}
        selectedKeys={[selectedKey]}
        className="DashboardMenu"
      />
      <div className={`SiderMenu__user ${collapsed ? 'SiderMenu__user--collapsed' : ''}`}>
        <ProfileDropdown userName={userName} email={email} collapsed={collapsed} />
      </div>
    </Sider>
  );
};

export default SiderMenu;
